import { useState } from 'react';
import cross from '~/assets/icons/cross.svg';
import { playSound, Sound } from '~/assets/sounds';
import { CurrencyIcon } from '~/components/collections';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import { Text } from '~/components/ui/Text';
import { TextInput } from '~/components/ui/TextInput';
import { useWalletIcon } from '~/hooks';
import { rage, useInputAction, useObservable } from '~/rage';
import { Currency, InputAction } from '~/shared';
import { setModalChangeNameVisible, useLayoutDispatch } from '../../redux';
import {
	StyledContainer,
	StyledGrid,
	StyledHeadContainer,
	StyledInfoContainer,
	StyledPriceContainer,
} from './ModalChangeName.styled';

const ModalChangeNameFormPanel = () => {
	const dispatch = useLayoutDispatch();
	const donateIcon = useWalletIcon(Currency.Donate, true);
	const changeNamePrice = useObservable(rage.player.getChangeNamePrice());

	const NAME_MIN_LENGTH = 3;
	const NAME_MAX_LENGTH = 14;
	const NAME_PATTERN = /^[A-Za-z]+$/;

	const sanitizeName = (value: string): string =>
		value.replace(/[^A-Za-z]/g, '').slice(0, NAME_MAX_LENGTH);

	const isValidName = (value: string): boolean =>
		NAME_PATTERN.test(value) &&
		value.length >= NAME_MIN_LENGTH &&
		value.length <= NAME_MAX_LENGTH;

	const [firstName, setFirstName] = useState<string>('');
	const [lastName, setLastName] = useState<string>('');
	const canSubmit = isValidName(firstName) && isValidName(lastName);

	const handleChange = () => {
		if (!canSubmit) {
			return;
		}

		rage.donate
			.changeName(firstName, lastName)
			.then(() => {
				dispatch(setModalChangeNameVisible(false));
				rage.notifications.showSuccess(
					'Заявка на смену имени отправлена на рассмотрение',
				);
			})
			.catch((err) => rage.notifications.showException(err));
	};

	useInputAction(InputAction.CommonMenuClose, () =>
		dispatch(setModalChangeNameVisible(false)),
	);
	useInputAction(InputAction.CommonMenuAccept, handleChange);

	return (
		<StyledContainer>
			<StyledHeadContainer>
				<Text>Смена никнейма</Text>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height20}
					borderRadius={ButtonBorderRadius.Radius5}
					style={{ width: 20 }}
					onClick={() => {
						dispatch(setModalChangeNameVisible(false));
						playSound(Sound.ClickButton);
					}}
				>
					<img src={cross} width={10} height={10} />
				</Button>
			</StyledHeadContainer>
			<Divider
				$direction={'H'}
				style={{ marginTop: 30, marginBottom: 30, opacity: 0.1 }}
			/>
			<Text style={{ fontSize: 16, color: '#ffffff', opacity: 0.75 }}>
				Введите свой новый никнейм в поле ниже
			</Text>
			<StyledGrid style={{ gap: 10, marginTop: 20 }}>
				<TextInput
					height={50}
					fontSize={16}
					placeholder={'Введите имя'}
					style={{ padding: '0 20px' }}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
					placeholderOpacity={0.5}
					minLength={NAME_MIN_LENGTH}
					maxLength={NAME_MAX_LENGTH}
					onChange={(e) => setFirstName(sanitizeName(e.target.value))}
					value={firstName}
				/>
				<TextInput
					height={50}
					fontSize={16}
					placeholder={'Введите фамилию'}
					style={{ padding: '0 20px' }}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
					placeholderOpacity={0.5}
					minLength={NAME_MIN_LENGTH}
					maxLength={NAME_MAX_LENGTH}
					onChange={(e) => setLastName(sanitizeName(e.target.value))}
					value={lastName}
				/>
			</StyledGrid>
			<StyledInfoContainer>
				<Text>Рассмотрение заявки смены ника до 3-х дней</Text>
			</StyledInfoContainer>
			<Divider
				$direction={'H'}
				style={{ marginTop: 20, marginBottom: 30, opacity: 0.1 }}
			/>
			<StyledPriceContainer>
				<Text>Стоимость</Text>
				<Text>{changeNamePrice.toLocaleString('ru-RU')}</Text>
				<CurrencyIcon icon={donateIcon} size="sm" />
			</StyledPriceContainer>
			<StyledGrid style={{ gap: 20, marginTop: 30 }}>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height50}
					onClick={() => {
						dispatch(setModalChangeNameVisible(false));
						playSound(Sound.ClickButton);
					}}
				>
					<Text>Отменить</Text>
				</Button>
				<Button
					color={ButtonColors.Buy}
					height={ButtonHeight.Height50}
					disabled={!canSubmit}
					onClick={() => {
						handleChange();
						playSound(Sound.ClickButton);
					}}
				>
					<Text>Оплатить</Text>
				</Button>
			</StyledGrid>
		</StyledContainer>
	);
};

export { ModalChangeNameFormPanel };
